<template>
  <div class="fcp-content">
    <div class="fcp-content__sections">
      <template v-for="(section, index) in sections" :key="section.status">
        <div
          v-if="index > 0"
          class="fcp-content__divider"
        />
        <FoundryContractSection
          :section="section"
          :isAdmin="isAdmin"
          :isSingleFoundry="isSingleFoundry"
          :isMixed="isMixed"
        />
      </template>
    </div>

    <div
      v-if="showLearnMoreLinks && hasLinks"
      class="fcp-content__links"
    >
      <div
        v-if="learnMoreLinks.terminationProcess"
        class="fcp-content__link-row"
        data-qa-id="foundry-contract-popup-learn-more-termination"
      >
        <MdTypography tag="span" variant="body2--regular" color="spirits--700">
          {{ $t('foundry_contract_popup.learn_more_termination') }}
        </MdTypography>
        <MdLink
          :href="learnMoreLinks.terminationProcess"
          target="_blank"
          rel="noopener noreferrer"
          color="blue"
          underline="false"
          inline
        >
          {{ $t('foundry_contract_popup.learn_more') }}
        </MdLink>
      </div>
      <div
        v-if="learnMoreLinks.reJoining"
        class="fcp-content__link-row"
        data-qa-id="foundry-contract-popup-learn-more-rejoin"
      >
        <MdTypography tag="span" variant="body2--regular" color="spirits--700">
          {{ $t('foundry_contract_popup.learn_more_rejoin') }}
        </MdTypography>
        <MdLink
          :href="learnMoreLinks.reJoining"
          target="_blank"
          rel="noopener noreferrer"
          color="blue"
          underline="false"
          inline
        >
          {{ $t('foundry_contract_popup.learn_more') }}
        </MdLink>
      </div>
    </div>

    <div v-if="showFooter" class="fcp-content__footer">
      <MdButton
        size="large"
        variant="none"
        color="lightgrey"
        type="button"
        :label="closeBtnLabel || $t('foundry_contract_popup.btn_close')"
        data-qa-id="foundry-contract-popup-btn-close"
        @handleclick="$emit('close')"
      >
        {{ closeBtnLabel || $t('foundry_contract_popup.btn_close') }}
      </MdButton>
      <MdButton
        v-if="showSeeChangesButton"
        size="large"
        variant="none"
        color="lightgrey"
        type="button"
        :label="$t('foundry_contract_popup.btn_see_changes')"
        data-qa-id="foundry-contract-popup-btn-see-changes"
        @handleclick="$emit('seeChanges')"
      >
        {{ $t('foundry_contract_popup.btn_see_changes') }}
      </MdButton>
      <MdButton
        v-if="showSignAgreementButton"
        size="large"
        variant="none"
        color="blue"
        type="button"
        :label="$t('foundry_contract_popup.btn_sign_agreement')"
        data-qa-id="foundry-contract-popup-btn-sign"
        @handleclick="$emit('signAgreement')"
      >
        <MdIconAgreements slot="icon-start" size="16px" color="white" />
        {{ $t('foundry_contract_popup.btn_sign_agreement') }}
      </MdButton>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { MdTypography, MdButton, MdIconAgreements, MdLink } from '@antiqua-dls/vue-components';
import FoundryContractSection from './FoundryContractSection.vue';

const props = defineProps({
  isAdmin: {
    type: Boolean,
    required: true
  },
  sections: {
    type: Array,
    required: true,
    validator: (val) => val.length >= 1 && val.length <= 2
  },
  learnMoreLinks: {
    type: Object,
    default: () => ({})
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  closeBtnLabel: {
    type: String,
    default: ''
  }
});

defineEmits(['close', 'seeChanges', 'signAgreement']);

const isSingleFoundry = computed(() =>
  props.sections.length === 1 && props.sections[0].foundries.length === 1
);

const isMixed = computed(() => props.sections.length === 2);

const hasTerminatingSection = computed(() =>
  props.sections.some(s => s.status === 'terminating')
);

const showSeeChangesButton = computed(() =>
  props.isAdmin && hasTerminatingSection.value
);

const showSignAgreementButton = computed(() => props.isAdmin);

const showLearnMoreLinks = computed(() => props.isAdmin);

const hasLinks = computed(() =>
  props.learnMoreLinks.terminationProcess || props.learnMoreLinks.reJoining
);
</script>

<style lang="scss" scoped>
@import './stylesheets/foundry-contract-popup';
.fcp-content {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  &__divider {
    display: none;
  }

  &__links {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__link-row {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    border-top: 1px solid var(--md-border-grey);
    padding-top: 24px;
  }
}

@include fcp-tablet {
  .fcp-content {
    gap: 16px;

    &__sections {
      gap: 32px;
    }
  }
}

@include fcp-mobile {
  .fcp-content {
    &__footer {
      flex-direction: column;
      gap: 16px;

      :deep(md-button) {
        width: 100%;
      }
    }
  }
}
</style>
